import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Trash2, Lock, ChevronLeft, ChevronRight } from "lucide-react";
import BulkUpdatePanel from "@/components/admin/BulkUpdatePanel";
import { ExportButton } from "@/components/admin/ExportButton";

const EMPTY = { name: "", slug: "", sku: "", category_id: "", brand: "", description: "", price: 0, currency: "TRY", stock: 0, image_url: "", featured: false, specs: {} };
const inp = "border border-ink-200 px-3 py-2";

function NewProductForm({ cats, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/products", { ...form, price: parseFloat(form.price), stock: parseInt(form.stock) });
      toast.success("Ürün eklendi"); setForm(EMPTY); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Hata"); }
  };
  return (
    <form onSubmit={submit} className="kz-card p-6 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
      <input required placeholder="Ad" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") })} className={inp}/>
      <input required placeholder="Slug" value={form.slug} onChange={set("slug")} className={inp}/>
      <input required placeholder="SKU" value={form.sku} onChange={set("sku")} className={inp}/>
      <select required value={form.category_id} onChange={set("category_id")} className={inp}>
        <option value="">Kategori seçin</option>
        {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <input placeholder="Marka" value={form.brand} onChange={set("brand")} className={inp}/>
      <div className="flex gap-2">
        <input type="number" step="0.01" placeholder="Liste (KDV hariç)" required value={form.price} onChange={set("price")} className={`${inp} flex-1`}/>
        <select value={form.currency} onChange={set("currency")} className={inp}>
          <option value="TRY">₺</option><option value="USD">$</option><option value="EUR">€</option>
        </select>
      </div>
      <input type="number" placeholder="Stok" value={form.stock} onChange={set("stock")} className={inp}/>
      <input placeholder="Görsel URL" value={form.image_url} onChange={set("image_url")} className={`md:col-span-2 ${inp}`}/>
      <textarea placeholder="Açıklama" rows={2} value={form.description} onChange={set("description")} className={`md:col-span-2 ${inp}`}/>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={set("featured")}/> Öne çıkarılsın</label>
      <button type="submit" className="kz-btn-primary md:col-span-2">Kaydet</button>
    </form>
  );
}

export default function ProductsTab({ cats, onChanged }) {
  const [filter, setFilter] = useState({ q: "", category_id: "", brand: "" });
  const [qInput, setQInput] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [brands, setBrands] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showBulk, setShowBulk] = useState(false);

  const load = () => api.get("/admin/products", { params: { ...filter, page, page_size: 50 } }).then(r => setData(r.data));
  useEffect(() => { load(); }, [filter, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/admin/brands").then(r => setBrands(r.data)); }, []);
  useEffect(() => { const t = setTimeout(() => { setPage(1); setFilter(f => ({ ...f, q: qInput.trim() })); }, 350); return () => clearTimeout(t); }, [qInput]);

  const setF = (k) => (e) => { setPage(1); setFilter({ ...filter, [k]: e.target.value }); };
  const del = async (id) => {
    if (!window.confirm("Bu ürünü silmek istediğinizden emin misiniz?")) return;
    await api.delete(`/products/${id}`); toast.success("Ürün silindi"); load(); onChanged();
  };

  return (
    <div>
      <div className="flex gap-2 mb-4 flex-wrap">
        <input data-testid="admin-product-search" value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Ürün adı veya SKU ara..." className={`${inp} flex-1 min-w-[200px]`}/>
        <select data-testid="admin-product-category" value={filter.category_id} onChange={setF("category_id")} className={inp}>
          <option value="">Tüm kategoriler</option>
          {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select data-testid="admin-product-brand" value={filter.brand} onChange={setF("brand")} className={inp}>
          <option value="">Tüm markalar</option>
          {brands.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <button data-testid="toggle-bulk-update" onClick={() => setShowBulk(!showBulk)} className={showBulk ? "kz-btn-dark text-sm py-2 px-4" : "kz-btn-outline text-sm py-2 px-4"}>Toplu Güncelle</button>
        <ExportButton path="/admin/export/products" filename="urunler.xlsx" label="Excel" testid="export-products"/>
        <button onClick={() => setShowNew(!showNew)} className="kz-btn-primary text-sm py-2 px-4"><Plus size={16}/> Yeni Ürün</button>
      </div>

      {showBulk && <BulkUpdatePanel filter={{ ...filter, category_name: cats.find(c => c.id === filter.category_id)?.name }} total={data.total} onDone={load}/>}
      {showNew && <NewProductForm cats={cats} onSaved={() => { setShowNew(false); load(); onChanged(); }}/>}

      <div className="kz-card overflow-x-auto">
        <table className="w-full">
          <thead className="bg-ink-100">
            <tr className="text-left text-xs uppercase tracking-wider">
              <th className="p-3">Ürün</th><th className="p-3">SKU</th><th className="p-3">SGS Liste</th><th className="p-3">Fark</th><th className="p-3">Stok</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan="6" className="p-10 text-center text-ink-500">Ürün bulunamadı</td></tr>}
            {data.items.map((p, i) => (
              <tr key={p.id} data-testid={`admin-product-row-${p.sku}`} className={i % 2 ? "bg-ink-100/50" : ""}>
                <td className="p-3 flex items-center gap-2 max-w-[420px]"><img src={p.image_url} alt="" className="w-10 h-10 object-cover bg-ink-100 shrink-0"/><span className="text-sm truncate">{p.name}</span></td>
                <td className="p-3 font-mono text-xs">{p.sku}</td>
                <td className="p-3 text-sm whitespace-nowrap">{p.price} {p.currency}</td>
                <td className="p-3 text-sm">{p.price_markup ? <span className={`font-bold ${p.price_markup > 0 ? "text-green-700" : "text-red-600"}`}>{p.price_markup > 0 ? "+" : ""}{Number(p.price_markup).toFixed(1)}%</span> : <span className="text-ink-300">—</span>}</td>
                <td className="p-3 text-sm whitespace-nowrap">{p.stock}{p.stock_locked && <Lock size={11} className="inline ml-1 text-brand" title="Manuel stok"/>}</td>
                <td className="p-3"><button data-testid={`delete-product-${p.id}`} onClick={() => del(p.id)} className="text-red-600 hover:text-red-800"><Trash2 size={16}/></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between p-3 border-t border-ink-200 text-xs text-ink-500">
          <span data-testid="admin-product-total">{data.total} ürün</span>
          <div className="flex items-center gap-2">
            <button data-testid="admin-product-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1 border border-ink-200 disabled:opacity-30"><ChevronLeft size={14}/></button>
            <span data-testid="admin-product-page">Sayfa {page} / {data.pages || 1}</span>
            <button data-testid="admin-product-next" disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="p-1 border border-ink-200 disabled:opacity-30"><ChevronRight size={14}/></button>
          </div>
        </div>
      </div>
    </div>
  );
}
